import { useNavigate } from "react-router-dom"
import { useMemo, useRef, useState } from "react"
import { FieldType, Form, FormField, ModalId, useUIActions, ConfirmWarningsModal } from "../../../../../features/ui"
import { useDeliveryHooks } from "../../../hooks/deliveryHooks"
import { TableName } from "../../../../../tables"
import { Customer, SaleOperation } from "../../../../sales"
import { DeliveryDataFields } from "../../delivery/delivery-data-fields/deliveryDataFields"
import { DeliveryData, DeliveryMethod } from "../../../state/types"
import { DeliveryOrderItemsTable } from "../delivery-order-items-table/deliveryOrderItemsTable"
import { DeliveryItemRow } from "../../types"
import { useActions } from "./actions"
import { deliveryHelpers } from "../../../hooks/deliveryHooks"
import { AddressQuickFormModal, useAddressBuilders } from "../../../../../features/address"
import { Data2 } from "../../../../../features/data-types"

type Props = {
    table: TableName
    operation: SaleOperation
    customer: Customer
}

export const EmitDeliveryOrderForm = (props: Props) => {
    const { table, operation } = props

    const defaultDeliveryData = useMemo(() => ({
        method: operation.deliveryData?.method,
        addressId: operation.deliveryData?.addressId,
        deliveryDate: deliveryHelpers.getDefaultDeliveryDate(operation.deliveryData?.deliveryDate)
    }), [operation.deliveryData])

    // Hoards are delivered partially, so their quantities start at zero and only what is sent is filled
    const isHoard = table === TableName.HOARD_ORDERS
    const defaultItemRows = useMemo(() => operation.items
        .filter(item => item.deliveryStats.pending > 0)
        .map(item => ({
            itemId: item.id,
            productId: item.productId,
            lotId: item.lotId,
            pending: item.deliveryStats.pending ?? 0,
            quantity: isHoard ? 0 : (item.deliveryStats.pending ?? 0)
        }))
    , [operation.items, isHoard])

    const [deliveryData, setDeliveryData] = useState<Partial<DeliveryData>>(defaultDeliveryData)
    const [orderItems, setOrderItems] = useState<DeliveryItemRow[]>(defaultItemRows)
    const [customer, setCustomer] = useState<Customer>(props.customer)
    const [newAddressId, setNewAddressId] = useState<string>()
    const [stockWarnings, setStockWarnings] = useState<string[]>([])
    const submittingRef = useRef(false)

    const { buildAddress } = useAddressBuilders()
    const { getPhysicalStockWarnings } = useDeliveryHooks()
    const { openModal } = useUIActions()
    const navigate = useNavigate()

    const { submitCustomerDeliveryAddress, emitDelivery } = useActions()

    const totalQuantity = useMemo(
        () => orderItems.reduce((sum, item) => sum + item.quantity, 0),
        [orderItems]
    )

    const fields: FormField[] = [
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente',
            render: () => <>{customer.name}</>
        },
        ...DeliveryDataFields({
            table,
            defaultData: deliveryData,
            customer,
            addressIdValue: newAddressId,
            branchId: operation.branchId,
            isMethodOptional: false,
            isAddressOptional: false,
            isDeliveryDateOptional: false,
            showSeparator: false,
            onChange: setDeliveryData
        }),
        {
            name: 'items',
            type: FieldType.TABLE,
            render: () => (
                <DeliveryOrderItemsTable
                    method={DeliveryMethod.SHIPMENT}
                    operation={operation}
                    itemRows={orderItems}
                    title="Productos a Emitir"
                    onChange={setOrderItems}
                />
            )
        }
    ]

    const submitDeliveryOrder = () => emitDelivery(
        table,
        operation.id,
        deliveryData,
        orderItems
    )

    const onSubmit = async () => {
        // The stock warnings load before the spinner shows up, so a second click could emit the order twice
        if (submittingRef.current) return
        submittingRef.current = true
        try {
            // Si no hay mercadería física (ej. vendida sin stock) se advierte, para no generar órdenes que no se pueden preparar
            const isReception = deliveryData.method === DeliveryMethod.RECEPTION
            const warnings = isReception ? [] : await getPhysicalStockWarnings(orderItems, operation.branchId)
            if (warnings.length > 0) {
                setStockWarnings(warnings)
                openModal(ModalId.WARNING_CONFIRM)
            } else {
                await submitDeliveryOrder()
            }
        } finally {
            submittingRef.current = false
        }
    }

    const onCancel = () => navigate(-1)

    // The new address is saved in the customer right away: otherwise the order points to an address that doesn't exist
    const onCreateCustomerAddress = async (data: Data2) => {
        const address = buildAddress(data)
        const savedCustomer = await submitCustomerDeliveryAddress(customer, address)
        setCustomer(savedCustomer)
        setNewAddressId(address.id)
        setDeliveryData({
            ...deliveryData,
            addressId: address.id
        })
    }

    return (
        <>
            <Form
                formId="emit-delivery-form"
                fields={fields}
                createLabel="Emitir Entrega"
                disableSubmit={totalQuantity === 0}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
            <AddressQuickFormModal onSubmit={onCreateCustomerAddress} />
            <ConfirmWarningsModal
                title="Sin mercadería en depósito"
                warnings={stockWarnings}
                description="La orden se puede emitir, pero la entrega no se va a poder concretar hasta que ingrese mercadería."
                submitLabel="Emitir de todas formas"
                onConfirm={submitDeliveryOrder}
            />
        </>
    )
}
